import * as React from "react"
import { Link, useLocation } from "react-router-dom"
import { styled } from "@mui/material/styles"
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp"
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion"
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary"
import MuiAccordionDetails from "@mui/material/AccordionDetails"
import { Box, Typography } from "@mui/material"

interface LeftSidebarMenu {
  toggleActive: () => void
}

// Custom styled Accordion components
const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}))

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#3a4252" : "#f6f7f9",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
}))

// Define the types for menu items
interface LinkItem {
  path: string
  title: string
  icon: string
}

interface AccordionItem {
  panel: string
  title: string
  icon: string
  badge?: string
  links: {
    path: string
    label: string
    badge?: string
    badgeStyle?: string
  }[]
}

type MenuItem = { section: string; items: (LinkItem | AccordionItem)[] }

const allMenuItems: MenuItem[] = [
  {
    section: "MAIN",
    items: [
      { path: "/", title: "Dashboard", icon: "dashboard" },
      { path: "/sales", title: "Sales", icon: "point_of_sale" },
      {
        path: "/website-orders",
        title: "Website Orders",
        icon: "shopping_cart",
      },
      {
        path: "/sales-return",
        title: "Sales Return",
        icon: "assignment_returned",
      },
      { path: "/cash-denominations", title: "Cash Denomination", icon: "payments" },
      { path: "/expenses", title: "Expenses", icon: "payments" },
    ],
  },

  {
    section: "Inventory",
    items: [
      {
        path: "/purchase",
        title: "Purchase",
        icon: "attach_money",
      },
      {
        path: "/stock-adjustment",
        title: "Stock Adjustment",
        icon: "edit",
      },
      { path: "/products", title: "Products", icon: "shopping_bag" },

      {
        path: "/product-variants",
        title: "Product Variants",
        icon: "Inventory_2",
      },
      { path: "/attributes", title: "Attributes", icon: "category" },
      { path: "/tags", title: "Tags", icon: "label" },
      { path: "/brands", title: "Brands", icon: "label" },
      {
        path: "/stock-transactions",
        title: "Stock Transaction",
        icon: "inventory",
      },
      {
        path: "/damage-products",
        title: "Damage Products",
        icon: "report_problem",
      },
      {
        path: "/footfall",
        title: "Footfall",
        icon: "accessibility",
      },
      {
        path: "/categories",
        title: "Categories",
        icon: "category",
      },
    ],
  },
  {
    section: "Business",
    items: [
      { path: "/customers", title: "Customers", icon: "people" },
      { path: "/suppliers", title: "Suppliers", icon: "storefront" },
      { path: "/stores", title: "Stores", icon: "store" },
      {
        panel: "report",
        title: "Reports",
        icon: "Verified_User",
        links: [
          { path: "/sales-report", label: "Sales" },
          { path: "/products-report", label: "Product" },
        ],
      },
    ],
  },
  {
    section: "User Managements",
    items: [
      { path: "/users", title: "Users", icon: "person" },
      // {
      //   panel: "userpanel",
      //   title: "Roles & Permission",
      //   icon: "Verified_User",
      //   links: [{ path: "/roles", label: "Roles" }],
      // },
    ],
  },
  {
    section: "System Setting",
    items: [
      // { path: "/payment-methods", title: "Payment Methods", icon: "person" },
      {
        panel: "configuration",
        title: "Configuration",
        icon: "settings",
        links: [
          { path: "/payment-methods", label: "Payment Methods" },
          // { path: "/reasons", label: "Reasons" },
          { path: "/attribute-names", label: "Attributes" },
          { path: "/inventory-configs", label: "Inventory" },
          { path: "/currency-config", label: "Currency" },
          { path: "/note-config", label: "Currency Note" },
          { path: "/website-config", label: "Website" },
        ],
      },
    ],
  },
]

const LeftSidebarMenu: React.FC<LeftSidebarMenu> = ({ toggleActive }) => {
  const location = useLocation()
  const [expanded, setExpanded] = React.useState<string | false>("")

  // Update expanded state based on location
  React.useEffect(() => {
    allMenuItems.forEach((section) => {
      section.items.forEach((item) => {
        if ("links" in item) {
          item.links.forEach((link) => {
            if (location.pathname === link.path) {
              setExpanded(item.panel)
            }
          })
        }
      })
    })
  }, [location.pathname])

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false)
    }

  return (
    <div className="leftSidebarDark">
      <div className="left-sidebar-menu">
        <div className="logo">
          <Link to="/">
            <img
              src="/images/logo-icon.svg"
              alt="logo-icon"
              width={26}
              height={26}
            />
            <span>POS</span>
          </Link>
        </div>

        <Box className="burger-menu" onClick={toggleActive}>
          <span className="top-bar"></span>
          <span className="middle-bar"></span>
          <span className="bottom-bar"></span>
        </Box>

        <div className="sidebar-inner">
          <div className="sidebar-menu">
            {allMenuItems.map((section, index) => (
              <div key={index}>
                <Typography
                  className="sub-title"
                  sx={{
                    fontWeight: "500",
                    textTransform: "uppercase",
                  }}
                >
                  {section.section}
                </Typography>
                {section.items.map((item) => {
                  if ("links" in item) {
                    return (
                      <Accordion
                        key={item.panel}
                        expanded={expanded === item.panel}
                        onChange={handleChange(item.panel)}
                        className="mat-accordion"
                      >
                        <AccordionSummary
                          className="mat-summary"
                          aria-controls={`${item.panel}d-content`}
                          id={`${item.panel}d-header`}
                        >
                          <i className="material-symbols-outlined">
                            {item.icon}
                          </i>
                          <span className="title">{item.title}</span>
                          {item.badge && (
                            <span className="trezo-badge">{item.badge}</span>
                          )}
                        </AccordionSummary>

                        <AccordionDetails className="mat-details">
                          <ul className="sidebar-sub-menu">
                            {item.links.map((link) => (
                              <li key={link.path} className="sidemenu-item">
                                <Link
                                  to={link.path}
                                  className={`sidemenu-link ${location.pathname === link.path ? "active" : ""}`}
                                >
                                  {link.label}
                                  {link.badge && (
                                    <span
                                      className={`trezo-badge ${link.badgeStyle || ""}`}
                                    >
                                      {link.badge}
                                    </span>
                                  )}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </AccordionDetails>
                      </Accordion>
                    )
                  } else {
                    // Determine if this menu item should be active
                    const isActive =
                      location.pathname === item.path ||
                      (item.path !== "/" &&
                        location.pathname.startsWith(item.path + "/"))

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`sidebar-menu-link ${isActive ? "active" : ""}`}
                      >
                        <i className="material-symbols-outlined">{item.icon}</i>
                        <span className="title">{item.title}</span>
                      </Link>
                    )
                  }
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LeftSidebarMenu
